'use client';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createProductReview } from '@eliadmin/core/src/api/products/product-api';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

// TODO: add translations
export const addReviewSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    review: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    rating: __eliadmin_zod_2()
      .int()
      .min(1, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE())
      .max(5, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE()),
  });

export type AddReviewDialogProps = {
  slug: string;
  product: ProductDetailData;
};

export function AddReviewDialog(props: AddReviewDialogProps) {
  const close = useDialogStore(s => s.close);
  const { t } = useTranslations();

  const openDialog = useDialogStore(state => state.open);

  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const createReviewMutation = useMutation({ mutationFn: createProductReview });

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addReviewSchema>>>({
    defaultValues: {
      rating: 1,
      review: '',
    },
    resolver: zodResolver(addReviewSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (!me?.authToken) return;

    try {
      await createReviewMutation.mutateAsync({
        authToken: me.authToken,
        body: data,
        locale,
        slug: props.slug,
      });
      toast({
        title: t('text648'),
        variant: 'success',
      });
      router.refresh();
      close(AddReviewDialog.id);
      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCTS_REVIEWS] });
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text661'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  return (
    <DialogContent
      className={cn(
        'w-full rounded-[24px] border-none px-4 py-6 sm:max-w-[818px] sm:px-6 sm:py-8 lg:px-12 lg:py-12',
        'ea-add-review-dialog__content',
      )}
    >
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-4',
          'ea-add-review-dialog',
        )}
      >
        <Form {...form}>
          <DialogHeader className={cn('ea-add-review-dialog__header')}>
            <DialogTitle
              className={cn(
                'font-sm:text-2xl text-xl font-medium tracking-tight',
                'ea-add-review-dialog__header-title',
              )}
            >
              {props.product.name}
            </DialogTitle>
          </DialogHeader>

          {!me?.authToken && (
            <div className="flex flex-col gap-4">
              <p className="">{t('text593')}</p>
              <Button
                variant="default"
                onClick={() => {
                  openDialog({
                    id: SignInDialog.id,
                    content: <SignInDialog />,
                  });
                }}
                asChild
                className="w-full md:w-auto md:flex-1"
              >
                {t('text189')}
              </Button>
            </div>
          )}

          {me?.authToken && (
            <div className="grid w-full grid-cols-2 gap-4 @container">
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn(
                      'col-span-2 flex w-full items-center justify-start gap-4',
                      'ea-add-review-dialog__rating-field',
                    )}
                  >
                    <FormLabel className={cn('m-0', 'ea-add-review-dialog__rating-label')}>
                      {t('text3514')}
                    </FormLabel>
                    <FormControl className={cn('', 'ea-add-review-dialog__rating-input')}>
                      <div className={cn('w-min', 'ea-product-review__rating-container')}>
                        <ProductRating
                          rating={field.value}
                          hideTextRating
                          onRatingChange={rating => form.setValue(field.name, rating)}
                          className={{
                            starDisabled: 'fill-white stroke-[#EDB000] stroke-[1px]',
                            starIcon: 'fill-[#EDB000] stroke-[#EDB000] stroke-[1px]',
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className={cn('', 'ea-add-review-dialog__rating-message')} />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="review"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn('col-span-2 w-full', 'ea-add-review-dialog__review-field')}
                  >
                    <FormControl className={cn('', 'ea-add-review-dialog__review-input')}>
                      <Textarea
                        {...field}
                        placeholder={t('text586')}
                        className="min-h-[150px] w-full rounded-[20px] border-none bg-black/[0.03] px-6 py-4 shadow-none"
                      />
                    </FormControl>
                    <FormMessage className={cn('', 'ea-add-review-dialog__review-message')} />
                  </FormItem>
                )}
              />
              <p className="w-fulltext-sm col-span-2 font-light text-black">
                {t('text2341')}{' '}
                <Link prefetch={false} href={ROUTES.GDPR()} className="font-semibold text-primary">
                  {t('text2342')}
                </Link>
              </p>
            </div>
          )}
          {me?.authToken && (
            <DialogFooter
              className={cn('flex w-full items-center justify-end', 'ea-add-review-dialog__footer')}
            >
              <Button
                type="submit"
                className={cn('w-full sm:w-fit', 'ea-add-review-dialog__footer-button')}
                isLoading={form.formState.isSubmitting}
              >
                {t('text2306')}
              </Button>
            </DialogFooter>
          )}
        </Form>
      </form>
    </DialogContent>
  );
}

AddReviewDialog.id = 'ADD_REVIEW_DIALOG';
